import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Grid,
    Paper,
    Typography,
    TableContainer,
    Table,
    TableBody,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Checkbox,
    Tabs, Tab,
} from '@mui/material';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import RepeatIcon from '@mui/icons-material/Repeat';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { selectedDayFilterList, MenuProps, fetchSchedulerPerformanceHub, exportSchedulerPerformanceHub } from '../WidgetService';
import { WidgetTimeFilterEnum } from '../../../Enum/WidgetTimeFilterEnum';
import { parentUrl } from '../../../utils/constants';
import SchedulerSummary from '../../../Models/Widget/SchedulerSummary.model';
import '../Widgets.css';

const filterList = selectedDayFilterList();

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: 'gray',
        color: theme.palette.common.white,
        padding: '10px',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        padding: '10px',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const SchedulerPerformanceHubDetail = () => {
    const history = useNavigate();
    const schedulingRole = 'Scheduling';
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [members, setMembers] = useState<SchedulerSummary>();
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [tabValue, setTabValue] = useState(0);
    const {
        schedulerSelectedProvider,
        setSchedulerSelectedProvider,
        schedulerDateFilter,
        setSchedulerDateFilter,
        schedulerFromDate,
        setSchedulerFromDate,
        schedulerToDate,
        setSchedulerToDate,
        schedulerShowInActiveProviders,
        setSchedulerShowInActiveProviders,
    } = useWidgetContext();
    const filteredProviders = (schedulerShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const schedulingProviders = filteredProviders.filter((p) => p.Role.RoleName === schedulingRole);

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
    }, [isFetched]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    function TabPanel(props) {
        const { children, value, index } = props;
        return (
            <div role="tabpanel" hidden={value !== index}>
                {value === index && <Box p={2}>{children}</Box>}
            </div>
        );
    }

    const handleChange = (event, newValue) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        if (schedulerSelectedProvider && (schedulerDateFilter !== WidgetTimeFilterEnum.Custom || (Boolean(schedulerFromDate) && Boolean(schedulerToDate)))) {
            const changeFilter = async () => {
                setIsMembersLoading(false);
                const data = await fetchSchedulerPerformanceHub(
                    schedulerSelectedProvider,
                    schedulerDateFilter,
                    Boolean(schedulerFromDate) && schedulerDateFilter === WidgetTimeFilterEnum.Custom ? new Date(schedulerFromDate).toISOString() : '',
                    Boolean(schedulerToDate) && schedulerDateFilter === WidgetTimeFilterEnum.Custom ? new Date(schedulerToDate).toISOString() : '',
                    true
                );
                setMembers(data);
                setIsMembersLoading(true);
                setIsFetched(true);
            };
            changeFilter();
        }

        if (schedulerDateFilter !== WidgetTimeFilterEnum.Custom && Boolean(schedulerFromDate) && Boolean(schedulerToDate)) {
            setSchedulerFromDate(null);
            setSchedulerToDate(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schedulerSelectedProvider, schedulerDateFilter, schedulerFromDate, schedulerToDate, schedulerShowInActiveProviders, setIsFetched]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const navigateToMemberDashboard = (id: any) => {
        window.open(`${parentUrl}provider/members-list/member-chart/${id}/scheduling`, '_blank');
    };

    const changeFromDate = (date: any) => {
        setSchedulerFromDate(date);
    };

    const changeToDate = (date: any) => {
        setSchedulerToDate(date);
    };

    const exportSchedulerSummaries = async () => {
        setDownLoading(true);
        const data = await exportSchedulerPerformanceHub(
            schedulerSelectedProvider,
            schedulerDateFilter,
            Boolean(schedulerFromDate) && schedulerDateFilter === WidgetTimeFilterEnum.Custom ? new Date(schedulerFromDate).toISOString() : '',
            Boolean(schedulerToDate) && schedulerDateFilter === WidgetTimeFilterEnum.Custom ? new Date(schedulerToDate).toISOString() : ''
        );
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `SchedulerPerformanceHub - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const toggleInactiveHandler = (isChecked: boolean) => {
        setSchedulerShowInActiveProviders(isChecked);
        setSchedulerSelectedProvider('');
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Scheduler Performance Hub
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    <Grid size={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Schedulers</InputLabel>
                            <Select
                                value={schedulerSelectedProvider}
                                label="Schedulers"
                                name="Schedulers"
                                onChange={(e) => setSchedulerSelectedProvider(e.target.value)}
                                MenuProps={MenuProps}
                            >
                                <FormControlLabel
                                    sx={{ pl: '12px', mb: '0px' }}
                                    control={<Checkbox checked={schedulerShowInActiveProviders} onChange={(e) => toggleInactiveHandler(e.target.checked)} />}
                                    label="Show Inactive"
                                />
                                {schedulingProviders.map((pro) => (
                                    <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                        {pro.LastName}, {pro.FirstName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select
                                value={schedulerDateFilter}
                                label="Date Filter"
                                name="Date"
                                onChange={(e) => setSchedulerDateFilter(parseInt(e.target.value as any))}
                            >
                                {filterList.map((date) => (
                                    <MenuItem key={date.Id} value={date.Id}>
                                        {date.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {parseInt(schedulerDateFilter) === WidgetTimeFilterEnum.Custom ? (
                        <React.Fragment>
                            <Grid size={1.5}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={Boolean(schedulerFromDate) ? schedulerFromDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeFromDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid size={1.5}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={Boolean(schedulerToDate) ? schedulerToDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => changeToDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                        </React.Fragment>
                    ) : null}
                    {parseInt(schedulerDateFilter) !== WidgetTimeFilterEnum.Custom ? <Grid size={7}></Grid> : <Grid size={4}></Grid>}
                    <Grid size={1}>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportSchedulerSummaries()}
                            variant="contained"
                            color="success"
                            disabled={!members?.ScheduledWithin48HoursOfOnboardingMembers?.length &&
                                !members?.NotScheduledWithin48HoursOfOnboardingMembers?.length &&
                                !members?.AverageDaysToFirstAppointmentAfterOnboardingDetail?.length &&
                                !members?.ConfirmedAppointmentsWithinThreeDays?.length &&
                                !members?.NotConfirmedAppointmentsWithinThreeDays?.length &&
                                !members?.RescheduledAppointmentsWithin7Days?.length &&
                                !members?.NotRescheduledAppointmentsWithin7Days?.length &&
                                !members?.RescheduledAppointmentsWithin14Days?.length &&
                                !members?.NotRescheduledAppointmentsWithin14Days?.length
                            }
                        >
                            {downLoading ? 'Exporting..' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2} paddingTop={2}>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {!isMembersLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={4}>
                                <Grid size={12} display="flex" justifyContent="center">
                                    <Box padding={2}>
                                        {/* Tabs Header */}
                                        <Tabs
                                            value={tabValue}
                                            onChange={handleChange}
                                            textColor="primary"
                                            indicatorColor="primary"
                                            variant="scrollable"
                                            scrollButtons="auto"
                                        >
                                            <Tab label={`Scheduled Within 48 Hours (${members?.ScheduledWithin48HoursOfOnboardingMembers?.length || 0})`} />
                                            <Tab label={`Avg Days To First Appointment (${members?.AverageDaysToFirstAppointmentAfterOnboardingDetail?.length || 0})`} />
                                            <Tab label={`Confirmed Within 3 Days (${members?.ConfirmedAppointmentsWithinThreeDays?.length || 0})`} />
                                            <Tab label={`Rescheduled Within 7 Days (${members?.RescheduledAppointmentsWithin7Days?.length || 0})`} />
                                            <Tab label={`Rescheduled Within 14 Days (${members?.RescheduledAppointmentsWithin14Days?.length || 0})`} />
                                        </Tabs>

                                        <TabPanel value={tabValue} index={0}>
                                            <Grid container spacing={4}>
                                                {/* First Table - Scheduled Within 48 Hours */}
                                                <Grid size={6}>
                                                    <Typography variant="h6" color="primary" textAlign="center" mb={1}>
                                                        Scheduled Within 48 Hours Of Onboarding Members ({members?.ScheduledWithin48HoursOfOnboardingMembers?.length || 0})
                                                    </Typography>
                                                    <TableContainer component={Paper} sx={{ height: '60vh' }}>
                                                        <Table aria-label="scheduled-within-48hrs" stickyHeader>
                                                            <TableHead>
                                                                <StyledTableRow>
                                                                    <StyledTableCell align="left">Member Name</StyledTableCell>
                                                                    <StyledTableCell align="left">Onboard Date</StyledTableCell>
                                                                    <StyledTableCell align="left">Scheduled On</StyledTableCell>
                                                                    <StyledTableCell align="left">Appointment On</StyledTableCell>
                                                                </StyledTableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {members?.ScheduledWithin48HoursOfOnboardingMembers?.length ? (
                                                                    members.ScheduledWithin48HoursOfOnboardingMembers.map((row, index) => (
                                                                        <StyledTableRow key={index} className="pointer" onClick={() => navigateToMemberDashboard(row.Id)}>
                                                                            <StyledTableCell align="left">{row.Name}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.DateInfo ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.ScheduledDate ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.AppointmentDate ?? '-'}
                                                                                {row.IsTemplate && (
                                                                                    <RepeatIcon
                                                                                        fontSize="small"
                                                                                        sx={{ ml: 1, verticalAlign: "middle", color: "primary.main" }}
                                                                                    />
                                                                                )}
                                                                            </StyledTableCell>
                                                                        </StyledTableRow>
                                                                    ))
                                                                ) : (
                                                                    <StyledTableRow>
                                                                        <StyledTableCell component="th" scope="row" colSpan={5}>
                                                                            No appointments found.
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>

                                                {/* Second Table - Not Scheduled Within 48 Hours */}
                                                <Grid size={6}>
                                                    <Typography variant="h6" color="primary" textAlign="center" mb={1}>
                                                        Not Scheduled Within 48 Hours Of Onboarding Members ({members?.NotScheduledWithin48HoursOfOnboardingMembers?.length || 0})
                                                    </Typography>
                                                    <TableContainer component={Paper} sx={{ height: '60vh' }}>
                                                        <Table aria-label="not-scheduled-within-48hrs" stickyHeader>
                                                            <TableHead>
                                                                <StyledTableRow>
                                                                    <StyledTableCell align="left">Member Name</StyledTableCell>
                                                                    <StyledTableCell align="left">Onboard Date</StyledTableCell>
                                                                    <StyledTableCell align="left">Scheduled On</StyledTableCell>
                                                                    <StyledTableCell align="left">Appointment On</StyledTableCell>
                                                                </StyledTableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {members?.NotScheduledWithin48HoursOfOnboardingMembers?.length ? (
                                                                    members.NotScheduledWithin48HoursOfOnboardingMembers.map((row, index) => (
                                                                        <StyledTableRow key={index} className="pointer" onClick={() => navigateToMemberDashboard(row.Id)}>
                                                                            <StyledTableCell align="left">{row.Name}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.DateInfo ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.ScheduledDate ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.AppointmentDate ?? '-'}
                                                                                {row.IsTemplate && (
                                                                                    <RepeatIcon
                                                                                        fontSize="small"
                                                                                        sx={{ ml: 1, verticalAlign: "middle", color: "primary.main" }}
                                                                                    />
                                                                                )}
                                                                            </StyledTableCell>
                                                                        </StyledTableRow>
                                                                    ))
                                                                ) : (
                                                                    <StyledTableRow>
                                                                        <StyledTableCell component="th" scope="row" colSpan={5}>
                                                                            No appointments found.
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>
                                            </Grid>
                                        </TabPanel>

                                        <TabPanel value={tabValue} index={1}>
                                            <Typography variant="h6" color="primary" textAlign="center" mb={1}>
                                                Average Days To First Appointment After Onboarding ({members?.AverageDaysToFirstAppointmentAfterOnboardingDetail?.length || 0})
                                            </Typography>
                                            <TableContainer component={Paper} sx={{ height: '60vh' }}>
                                                <Table aria-label="customized table" stickyHeader>
                                                    <TableHead>
                                                        <StyledTableRow>
                                                            <StyledTableCell align="left">Member Name</StyledTableCell>
                                                            <StyledTableCell align="left">Onboarded On</StyledTableCell>
                                                            <StyledTableCell align="left">Appointment On</StyledTableCell>
                                                            <StyledTableCell align="left">Days Difference</StyledTableCell>
                                                        </StyledTableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {members?.AverageDaysToFirstAppointmentAfterOnboardingDetail?.length ? (
                                                            members.AverageDaysToFirstAppointmentAfterOnboardingDetail.map((row, index) => (
                                                                <StyledTableRow className="pointer" onClick={() => navigateToMemberDashboard(row.Id)}>
                                                                    <StyledTableCell align="left">{row.Name}</StyledTableCell>
                                                                    <StyledTableCell align="left">{row.DateInfo !== null ? row.DateInfo : '-'}</StyledTableCell>
                                                                    <StyledTableCell align="left">
                                                                        {row.AppointmentDate !== null ? row.AppointmentDate : '-'}
                                                                    </StyledTableCell>
                                                                    <StyledTableCell align="left">{row.AverageDays}</StyledTableCell>
                                                                </StyledTableRow>
                                                            ))
                                                        ) : (
                                                            <StyledTableRow>
                                                                <StyledTableCell component="th" scope="row" colSpan={5}>
                                                                    No appointments found.
                                                                </StyledTableCell>
                                                            </StyledTableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </TabPanel>

                                        <TabPanel value={tabValue} index={2}>
                                            <Grid container spacing={4}>
                                                {/* Confirmed Appointments */}
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography variant="h6" color="primary" textAlign="center" mb={1}>
                                                        Confirmed Appointments Within 3 Days ({members?.ConfirmedAppointmentsWithinThreeDays?.length || 0})
                                                    </Typography>
                                                    <TableContainer component={Paper} sx={{ height: '60vh' }}>
                                                        <Table aria-label="confirmed-appointments" stickyHeader>
                                                            <TableHead>
                                                                <StyledTableRow>
                                                                    <StyledTableCell align="left">Member Name</StyledTableCell>
                                                                    <StyledTableCell align="left">Appointment On</StyledTableCell>
                                                                    <StyledTableCell align="left">Confirmed On</StyledTableCell>
                                                                </StyledTableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {members?.ConfirmedAppointmentsWithinThreeDays?.length ? (
                                                                    members.ConfirmedAppointmentsWithinThreeDays.map((row, index) => (
                                                                        <StyledTableRow key={index} className="pointer" onClick={() => navigateToMemberDashboard(row.Id)}>
                                                                            <StyledTableCell align="left">{row.Name}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.AppointmentDate ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.DateInfo ?? '-'}</StyledTableCell>
                                                                        </StyledTableRow>
                                                                    ))
                                                                ) : (
                                                                    <StyledTableRow>
                                                                        <StyledTableCell component="th" scope="row" colSpan={3}>
                                                                            No appointments found.
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>

                                                {/* Not Confirmed Appointments */}
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography variant="h6" color="primary" textAlign="center" mb={1}>
                                                        Not Confirmed Appointments Within 3 Days ({members?.NotConfirmedAppointmentsWithinThreeDays?.length || 0})
                                                    </Typography>
                                                    <TableContainer component={Paper} sx={{ height: '60vh' }}>
                                                        <Table aria-label="not-confirmed-appointments" stickyHeader>
                                                            <TableHead>
                                                                <StyledTableRow>
                                                                    <StyledTableCell align="left">Member Name</StyledTableCell>
                                                                    <StyledTableCell align="left">Appointment On</StyledTableCell>
                                                                    <StyledTableCell align="left">Confirmed On</StyledTableCell>
                                                                </StyledTableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {members?.NotConfirmedAppointmentsWithinThreeDays?.length ? (
                                                                    members.NotConfirmedAppointmentsWithinThreeDays.map((row, index) => (
                                                                        <StyledTableRow key={index} className="pointer" onClick={() => navigateToMemberDashboard(row.Id)}>
                                                                            <StyledTableCell align="left">{row.Name}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.AppointmentDate ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.DateInfo ?? '-'}</StyledTableCell>
                                                                        </StyledTableRow>
                                                                    ))
                                                                ) : (
                                                                    <StyledTableRow>
                                                                        <StyledTableCell component="th" scope="row" colSpan={3}>
                                                                            No appointments found.
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>
                                            </Grid>
                                        </TabPanel>

                                        <TabPanel value={tabValue} index={3}>
                                            <Grid container spacing={4}>
                                                {/* Rescheduled Appointments Within 7 Days */}
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography variant="h6" color="primary" textAlign="center" mb={1}>
                                                        Rescheduled Appointments Within 7 Days ({members?.RescheduledAppointmentsWithin7Days?.length || 0})
                                                    </Typography>
                                                    <TableContainer component={Paper} sx={{ height: '60vh' }}>
                                                        <Table aria-label="rescheduled-appointments" stickyHeader>
                                                            <TableHead>
                                                                <StyledTableRow>
                                                                    <StyledTableCell align="left">Member Name</StyledTableCell>
                                                                    <StyledTableCell align="left">Appointment On</StyledTableCell>
                                                                    <StyledTableCell align="left">Rescheduled On</StyledTableCell>
                                                                </StyledTableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {members?.RescheduledAppointmentsWithin7Days?.length ? (
                                                                    members.RescheduledAppointmentsWithin7Days.map((row, index) => (
                                                                        <StyledTableRow
                                                                            key={index}
                                                                            className="pointer"
                                                                            onClick={() => navigateToMemberDashboard(row.Id)}
                                                                        >
                                                                            <StyledTableCell align="left">{row.Name}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.AppointmentDate ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.DateInfo ?? '-'}</StyledTableCell>
                                                                        </StyledTableRow>
                                                                    ))
                                                                ) : (
                                                                    <StyledTableRow>
                                                                        <StyledTableCell component="th" scope="row" colSpan={3}>
                                                                            No appointments found.
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>

                                                {/* Not Rescheduled Appointments Within 7 Days */}
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography variant="h6" color="primary" textAlign="center" mb={1}>
                                                        Not Rescheduled Appointments Within 7 Days ({members?.NotRescheduledAppointmentsWithin7Days?.length || 0})
                                                    </Typography>
                                                    <TableContainer component={Paper} sx={{ height: '60vh' }}>
                                                        <Table aria-label="not-rescheduled-appointments" stickyHeader>
                                                            <TableHead>
                                                                <StyledTableRow>
                                                                    <StyledTableCell align="left">Member Name</StyledTableCell>
                                                                    <StyledTableCell align="left">Appointment On</StyledTableCell>
                                                                    <StyledTableCell align="left">Rescheduled On</StyledTableCell>
                                                                </StyledTableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {members?.NotRescheduledAppointmentsWithin7Days?.length ? (
                                                                    members.NotRescheduledAppointmentsWithin7Days.map((row, index) => (
                                                                        <StyledTableRow
                                                                            key={index}
                                                                            className="pointer"
                                                                            onClick={() => navigateToMemberDashboard(row.Id)}
                                                                        >
                                                                            <StyledTableCell align="left">{row.Name}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.AppointmentDate ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.DateInfo ?? '-'}</StyledTableCell>
                                                                        </StyledTableRow>
                                                                    ))
                                                                ) : (
                                                                    <StyledTableRow>
                                                                        <StyledTableCell component="th" scope="row" colSpan={3}>
                                                                            No appointments found.
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>
                                            </Grid>
                                        </TabPanel>

                                        <TabPanel value={tabValue} index={4}>
                                            <Grid container spacing={4}>
                                                {/* Rescheduled Appointments Within 14 Days */}
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography variant="h6" color="primary" textAlign="center" mb={1}>
                                                        Rescheduled Appointments Within 14 Days ({members?.RescheduledAppointmentsWithin14Days?.length || 0})
                                                    </Typography>
                                                    <TableContainer component={Paper} sx={{ height: '60vh' }}>
                                                        <Table aria-label="rescheduled-appointments-14" stickyHeader>
                                                            <TableHead>
                                                                <StyledTableRow>
                                                                    <StyledTableCell align="left">Member Name</StyledTableCell>
                                                                    <StyledTableCell align="left">Appointment On</StyledTableCell>
                                                                    <StyledTableCell align="left">Rescheduled On</StyledTableCell>
                                                                </StyledTableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {members?.RescheduledAppointmentsWithin14Days?.length ? (
                                                                    members.RescheduledAppointmentsWithin14Days.map((row, index) => (
                                                                        <StyledTableRow
                                                                            key={index}
                                                                            className="pointer"
                                                                            onClick={() => navigateToMemberDashboard(row.Id)}
                                                                        >
                                                                            <StyledTableCell align="left">{row.Name}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.AppointmentDate ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.DateInfo ?? '-'}</StyledTableCell>
                                                                        </StyledTableRow>
                                                                    ))
                                                                ) : (
                                                                    <StyledTableRow>
                                                                        <StyledTableCell component="th" scope="row" colSpan={3}>
                                                                            No appointments found.
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>

                                                {/* Not Rescheduled Appointments Within 14 Days */}
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography variant="h6" color="primary" textAlign="center" mb={1}>
                                                        Not Rescheduled Appointments Within 14 Days ({members?.NotRescheduledAppointmentsWithin14Days?.length || 0})
                                                    </Typography>
                                                    <TableContainer component={Paper} sx={{ height: '60vh' }}>
                                                        <Table aria-label="not-rescheduled-appointments-14" stickyHeader>
                                                            <TableHead>
                                                                <StyledTableRow>
                                                                    <StyledTableCell align="left">Member Name</StyledTableCell>
                                                                    <StyledTableCell align="left">Appointment On</StyledTableCell>
                                                                    <StyledTableCell align="left">Rescheduled On</StyledTableCell>
                                                                </StyledTableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {members?.NotRescheduledAppointmentsWithin14Days?.length ? (
                                                                    members.NotRescheduledAppointmentsWithin14Days.map((row, index) => (
                                                                        <StyledTableRow
                                                                            key={index}
                                                                            className="pointer"
                                                                            onClick={() => navigateToMemberDashboard(row.Id)}
                                                                        >
                                                                            <StyledTableCell align="left">{row.Name}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.AppointmentDate ?? '-'}</StyledTableCell>
                                                                            <StyledTableCell align="left">{row.DateInfo ?? '-'}</StyledTableCell>
                                                                        </StyledTableRow>
                                                                    ))
                                                                ) : (
                                                                    <StyledTableRow>
                                                                        <StyledTableCell component="th" scope="row" colSpan={3}>
                                                                            No appointments found.
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>
                                            </Grid>
                                        </TabPanel>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default SchedulerPerformanceHubDetail;
